'use client'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

type RevealDelay = 0 | 1 | 2 | 3 | 4 | 5

interface RevealProps {
  /** Stagger step within one section — 80ms apart, capped at 6 steps */
  delay?: RevealDelay
  className?: string
  children: React.ReactNode
}

const DELAY_CLASSES: Record<RevealDelay, string> = {
  0: 'delay-0',
  1: 'delay-[80ms]',
  2: 'delay-[160ms]',
  3: 'delay-[240ms]',
  4: 'delay-[320ms]',
  5: 'delay-[400ms]',
}

/**
 * Scroll-entrance reveal — blocks rise in one by one as they enter the
 * viewport (GoodNotes grammar). Ease-out-expo curve, 16px rise, per-block
 * IntersectionObserver so stacked cards fire independently.
 */
export function Reveal({ delay = 0, className, children }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState<boolean>(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return
    if (typeof IntersectionObserver === 'undefined') {
      const frame = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(frame)
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '0px 0px -10% 0px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={cn(
        'js-reveal transition-[opacity,translate] duration-700 [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none',
        DELAY_CLASSES[delay],
        visible
          ? 'translate-y-0 opacity-100'
          : 'translate-y-4 opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100',
        className
      )}
    >
      {children}
    </div>
  )
}
